import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from 'react';
import { LassoTab, LassoTabs } from "../../../components/LassoTabs";
import ImagingFiles from "./ImagingFiles";
import TabularFiles from "./TabularFiles";
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SdStorageOutlinedIcon from '@mui/icons-material/SdStorageOutlined';
import {
    setParticipantLimit,
    selectParticipantLimit,
    selectIsRedirected, setIsRedirected, adwTransferFiles
} from "../../data-wrangler/dataWranglerSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectFileSummary } from "../../data-wrangler/dataWranglerSlice";
import ToolSelectModal from "./ToolSelectModal";
import { selectSelectedDatabase } from "../../common/databaseSlice";
import ConcatFiles from "./ConcatFiles";
import { useWizardQuery } from "../../../api/dataWranglerApiSlice";
import LassoToggleButtons from "../../../components/LassoToggleButtons";
import { useUserInfoQuery } from "../../../api/userApiSlice";
import SummarySnackbar from './SummarySnackbar';
import ToolSelector from "../../data-wrangler/components/ToolSelector";
import { LassoTextField } from "../../../components/LassoTextField";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export enum selections {
    Imaging = "imaging",
    Tabular = "tabular",
    Concat = "concat"
}

const FileDownloadDashboard = () => {
    const { data: userData } = useUserInfoQuery({});
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const isGuestUser = userData?.id === -1
    const { data } = useWizardQuery({ database: selectedDatabase }, { skip: isGuestUser });
    const [study,setStudy] = useState(data?.file_datasets[0].name);
    const [currentSection, setCurrentSection] = useState(selections.Imaging);
    const [open, setOpen] = useState(false);
    const participantLimit = useSelector(selectParticipantLimit);
    const fileTransferSummary = useSelector(selectFileSummary);
    const isRedirectedFromDD = useSelector(selectIsRedirected);
    const dispatch = useDispatch();
    const role = userData ? userData.roles : ["SUMMARY"];

    useEffect(() => {
        if(isRedirectedFromDD) {
            setCurrentSection(selections.Tabular);
            dispatch(setIsRedirected(false))
        }
    }, [isRedirectedFromDD]);

    const participantCount = fileTransferSummary.participantCount || 0;

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // The number type in Textfield allows characters - + e
        // iPads also need a custom validation since input numbers accepts alpha and special characters
        if (!((e.key >= '0' && e.key <= '9') || e.key === '.' || e.key === 'Backspace')) {
            e.preventDefault();
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        (value > participantCount) ?
            dispatch(setParticipantLimit(participantCount)) :
            dispatch(setParticipantLimit(value));
    };

    const hasDownloadPermission = role.includes("ADMIN") || (role.includes("SUBJECT") && !window?.env?.onlyDuc);
    const isTriggerDisabled = !hasDownloadPermission || fileTransferSummary.fileCount === 0;
    const isParticipantLimitDisabled = isTriggerDisabled || participantCount < 1;

    useEffect(() => {
        if(isParticipantLimitDisabled) {
            dispatch(setParticipantLimit(null));
        }
    }, [isParticipantLimitDisabled]);

    const triggerButtonTooltip =
        (window?.env?.onlyDuc && !role.includes("ADMIN")) ?
            "This button is temporarily disabled, when full data download is available it will be open for users who have a valid DUC and have passed training" :
            !role.includes("SUBJECT") && !role.includes("ADMIN") ?
                "Complete Data Use Certification (DUC) to access all features" :
                fileTransferSummary.fileCount === 0 ?
                    'Please select files relevant to your analysis and hit "Trigger Transfer" to initiate data download.' :
                    "";

    const datasetsOptions = useMemo(() => {
        return data?.file_datasets.map(({ name: ds }: {name: string}) =>
            ({ label: ds, value: ds })
        );
    }, [data]);

    useEffect(() => {
        setStudy(data?.file_datasets[0].name);
    }, [data]);

    const handleTabChange = (event: React.SyntheticEvent, newTab: selections) => {
        setCurrentSection(newTab)
    }

    const renderSections = (study: string) => {
        switch (currentSection) {
        case selections.Imaging:
            return (<ImagingFiles study={study}/>)
        case selections.Tabular:
            return (<TabularFiles study={study}/>)
        case selections.Concat:
            return (<ConcatFiles study={study}/>)

        }
    }

    const onSubmitGlobus = () => {
        dispatch(adwTransferFiles());
    }

    return (
        <Stack gap={4} marginLeft="10px" height="100%">
            <Stack flexDirection="row" justifyContent="space-between">
                <LassoTabs value={currentSection}  onChange={handleTabChange}>
                    <LassoTab label="Imaging and Wearables" value= {selections.Imaging}/>
                    <LassoTab label="Tabulated" value={selections.Tabular}/>
                    <LassoTab label="Concat" value={selections.Concat}/>
                </LassoTabs>

                <ToolSelector />
            </Stack>

            <LassoToggleButtons
                size="small"
                variant="tiny"
                color="primary"
                value={study}
                onChange={(e, newValue) => newValue && setStudy(newValue)}
                exclusive
                buttons={(datasetsOptions || []).map((option: any) => {
                    return { ...option,
                        disabled: false }
                })}
            />
            <Stack gap = {2} flexDirection="row" justifyContent="space-between">
                <Stack>
                    <Typography fontWeight="500" fontSize="24px">
                        Select Files to Download
                    </Typography>
                    <Stack flexDirection="row" gap = {3}>
                        <Stack flexDirection="row">
                            <Typography fontSize="16px">
                                Participants (Imaging)
                            </Typography>
                            <Chip
                                icon={<PeopleAltOutlinedIcon/>}
                                variant="outlined"
                                size="small"
                                style={{ marginLeft: "5px", padding: "7px" }}
                                label={participantCount}
                                color="primary"
                            />
                        </Stack>
                        <Stack flexDirection="row">
                            <Typography fontSize="16px">
                                Files
                            </Typography>
                            <Chip
                                icon={<ContentCopyOutlinedIcon/>}
                                variant="outlined"
                                size="small"
                                style={{ marginLeft: "5px", padding: "7px" }}
                                label={fileTransferSummary.fileCount}
                                color="primary"
                            />
                        </Stack>
                        <Stack flexDirection="row">
                            <Typography fontSize="16px">
                                Download Size
                            </Typography>
                            <Chip
                                icon={<SdStorageOutlinedIcon/>}
                                color="primary"
                                variant="outlined"
                                size="small"
                                style={{ marginLeft: "5px", padding: "7px" }}
                                label={fileTransferSummary.fileSize}
                            />
                        </Stack>
                    </Stack>
                </Stack>

                <Stack flexDirection="row" height="43px" gap={3}>
                    <div style={{ display: 'none' }}>
                        <LassoTextField
                            type="number"
                            sx={{ width: "220px" }}
                            label="Number of participants (optional)"
                            disabled={isParticipantLimitDisabled}
                            onKeyDown={onKeyDown}
                            onChange={onChange}
                            value={participantLimit || ""}
                            InputProps={{
                                inputProps: {
                                    max: participantCount,
                                    min: 0,
                                }
                            }}
                        />
                    </div>
                    <Tooltip title={triggerButtonTooltip} arrow>
                        <span>
                            <Button
                                variant="primary"
                                disabled={isTriggerDisabled}
                                onClick={() => setOpen(true)}>
                                Trigger Transfer
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        data-testid="file-download-dashboard-button-view"
                        variant={"outlined"}
                        href={"https://app.globus.org/activity"}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                        disabled={!hasDownloadPermission}
                        endIcon={<OpenInNewIcon/>}
                    >
                        View Transfer Status
                    </Button>
                </Stack>
            </Stack>
            <Stack height="100%">
                {renderSections(study)}
            </Stack>
            <ToolSelectModal open={open} setOpen={setOpen} onsubmit={onSubmitGlobus}/>
            <SummarySnackbar/>
        </Stack>
    )
}

export default FileDownloadDashboard